import React, { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Bank, Trash, X, Lock } from "@phosphor-icons/react";

const CCYS = ["USD", "EUR", "GBP", "AED", "INR", "JPY", "CHF", "SGD"];

export default function BankAccountsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", bank_name: "", account_number: "", currency: "USD", opening_balance: "0" });

  const load = async () => { const { data } = await api.get("/bank-accounts"); setRows(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/bank-accounts", { ...form, opening_balance: parseFloat(form.opening_balance || "0") });
    setOpen(false);
    setForm({ name: "", bank_name: "", account_number: "", currency: "USD", opening_balance: "0" });
    load();
  };
  const remove = async (id) => { await api.delete(`/bank-accounts/${id}`); load(); };

  return (
    <div>
      <PageHeader
        eyebrow="Cash"
        title="Bank Accounts"
        subtitle="Multi-currency bank accounts with live computed balances."
        testid="bank-title"
        actions={
          <button onClick={() => setOpen(true)} data-testid="new-bank-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
            <Plus size={14} weight="bold" /> New Account
          </button>
        }
      />

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((a) => (
          <div key={a.id} className="border border-gray-200 bg-white p-6 hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`bank-card-${a.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Bank size={18} weight="bold" />
                  <div className="font-black text-base">{a.name}</div>
                </div>
                <div className="text-xs text-gray-600 mt-1">{a.bank_name}</div>
                <div className="font-mono text-xs text-gray-500 mt-1">{a.account_number}</div>
              </div>
              <button onClick={() => remove(a.id)} data-testid={`del-bank-${a.id}`}><Trash size={14} className="text-[#DC2626]" /></button>
            </div>
            <div className="mt-6 border-t border-gray-200 pt-4">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Current Balance</div>
              <div className={`text-2xl font-black font-mono mt-1 ${a.current_balance < 0 ? "text-[#DC2626]" : ""}`}>
                {fmt(a.current_balance)} <span className="text-xs text-gray-500 font-medium">{a.currency}</span>
              </div>
              {a.locked_collateral > 0 && (
                <div className="mt-3 flex items-center justify-between text-xs" data-testid={`bank-locked-${a.id}`}>
                  <span className="inline-flex items-center gap-1 text-[#EAB308] font-semibold">
                    <Lock size={12} weight="bold" /> Locked: {fmt(a.locked_collateral)} {a.currency}
                  </span>
                  <span className="text-gray-500">Available: <span className="font-mono font-semibold text-gray-800">{fmt(a.available_balance)}</span></span>
                </div>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full text-center text-sm text-gray-500 border border-dashed border-gray-300 py-16">
            No bank accounts yet. Create one to start tracking cash positions.
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="bank-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-black tracking-tight">New Bank Account</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["name", "Account Name", "text"],
                ["bank_name", "Bank Name", "text"],
                ["account_number", "Account Number", "text"],
                ["opening_balance", "Opening Balance", "number"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required={k !== "opening_balance"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`bank-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Currency</label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="bank-currency" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  {CCYS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="bank-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create account</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
